import { useRef, useEffect } from 'react';

const getFocusableElements = (container: HTMLElement): HTMLElement[] => {
    const focusableElements = Array.from(
        container.querySelectorAll<HTMLElement>(
            'button, [href], textarea, input, select,[tabindex]:not([tabindex="-1"])'
        )
    );

    const filteredFocusableElements = focusableElements.filter((element: HTMLElement) => {
        const elementStyle = window.getComputedStyle(element);
        return (
            element.tabIndex >= 0 &&
            !element.hasAttribute('disabled') &&
            elementStyle.display !== 'none' &&
            elementStyle.visibility !== 'hidden'
        );
    });

    return filteredFocusableElements;
};

export const usePanelFocusTrap = <T extends HTMLElement = HTMLDivElement>() => {
    const panelBorderRef = useRef<T | null>(null);
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            const panelBorder = panelBorderRef.current;
            if (!panelBorder) return;

            const focusableElements = getFocusableElements(panelBorder);

            if (focusableElements.length === 0) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            if (event.key === 'Tab') {
                if (event.shiftKey && document.activeElement === firstElement) {
                    lastElement.focus();
                    event.preventDefault();
                } else if (!event.shiftKey && document.activeElement === lastElement) {
                    firstElement.focus();
                    event.preventDefault();
                }
            }
        };

        const panelBorder = panelBorderRef.current;
        panelBorder?.addEventListener('keydown', handleKeyDown);
        return () => {
            panelBorder?.removeEventListener('keydown', handleKeyDown);
        };
    }, [panelBorderRef]);

    return panelBorderRef;
};
